import { Station } from "@/entities/station";
import { normalizeForSearch, removeDiacritics } from "@/shared/utils";
import commandScore from "command-score";

type FilterFn = (value: string, search: string, keywords?: string[]) => number;

const BASE_WEIGHT = 0.6;
const TRAJECT_WEIGHT = 0.3;
const INDEX_WEIGHT = 0.1;

/**
 * Maakt een filterfunctie die stations beoordeelt op relevantie (0–1).
 *
 * @param {Station[]} preferredStations - Stations die voorkeur verdienen boven andere stations.
 * @param {Station[]} allStations - Alle beschikbare stations.
 * @returns {(value: string, search: string, keywords: string[]) => number} - Een functie die een station-code of naam evalueert (NB: dit is de _label_ van de option!!)
 *
 */
export function createStationsRankFunction(preferredStations: Station[], allStations: Station[]): FilterFn {
    return function filterFn(value: string, search: string, keywords?: string[]): number {
        const normalizedSearch = normalizeForSearch(search);
        const normalizedValue = normalizeForSearch(value);

        const station = allStations.find((s) => {
            const displayNameMatch = normalizeForSearch(Station.getDisplayName(s, "stationCodeSuffixed")) === normalizedValue;
            const nameMatch = normalizeForSearch(s.langeNaam) === normalizedValue;
            const codeMatch = normalizeForSearch(s.code) === normalizedValue;
            return nameMatch || codeMatch || displayNameMatch;
        });

        const trajectIndex = preferredStations.findIndex((s) => s.code === station?.code);
        const inTraject = trajectIndex >= 0;
        const nlStationMultiplier = station && station.landCode == "NL" ? 1 : 0.5;

        // fallback (partial matches and fuzzy matching)
        const fuzzyScore = commandScore(
            normalizedValue,
            normalizedSearch,
            [station?.code, station?.langeNaam, ...(keywords ?? [])].filter((i): i is string => Boolean(i)).map(removeDiacritics)
        );

        // No search value → only show traject stations (if exists)
        if (!normalizedSearch && preferredStations.length > 0) {
            return inTraject ? indexMultiplier(trajectIndex, preferredStations.length) : 0;
        }

        // Exacte match op code
        if (station && normalizeForSearch(station.code) === normalizedSearch) {
            return 1 * nlStationMultiplier;
        }

        // Exacte match op naam
        if (station && normalizeForSearch(station.langeNaam) === normalizedSearch) {
            return 0.9 * nlStationMultiplier;
        }

        // Search value is non-empty → rank traject stations higher
        if (station && normalizeForSearch(station.code).includes(normalizedSearch)) {
            const indexComponent = stringIndexMultiplier(normalizedSearch, normalizeForSearch(station.code));
            const score = 0.8 * nlStationMultiplier * BASE_WEIGHT + (inTraject ? TRAJECT_WEIGHT : 0) + indexComponent * INDEX_WEIGHT;
            return Math.min(1, score);
        }

        // Substring match op naam
        if (station && normalizeForSearch(station.langeNaam).includes(normalizedSearch)) {
            const indexComponent = stringIndexMultiplier(normalizedSearch, normalizeForSearch(station.langeNaam));
            const score = 0.7 * nlStationMultiplier * BASE_WEIGHT + (inTraject ? TRAJECT_WEIGHT : 0) + indexComponent * INDEX_WEIGHT;
            return Math.min(1, score);
        }

        const baseScore = Math.min(0.6, Math.max(0, fuzzyScore));
        const finalScore = baseScore * nlStationMultiplier * BASE_WEIGHT + (inTraject ? TRAJECT_WEIGHT : 0);
        return Math.min(1, finalScore);
    };
}

const indexMultiplier = (index: number, length: number) => (index >= 0 ? 1 - index / length / 10 : 0);

function stringIndexMultiplier(substring: string, fullString: string) {
    const index = fullString.indexOf(substring);
    return indexMultiplier(index, fullString.length);
}
